import { cva } from 'class-variance-authority';
import { Button, Flex, Spin, Text } from '@nula/core-ui';
import type { FailureCopy } from '@/lib/templates/failure-copy';
import {
  FROM_EXCERPT_LINE,
  REVIEW_LABELS,
  STALE_PROPOSAL_LINE,
  waitingLine,
} from '@/lib/templates/review-copy';
import styles from './surface-proposal-bar.module.css';

const bar = cva(styles.bar, {
  variants: { progressing: { true: styles.progressing, false: '' } },
  defaultVariants: { progressing: false },
});

export type SurfaceProposalBarProps = (
  | {
      kind: 'waiting';
      templateName: string | null;
      fromExcerpt: boolean;
      canEdit: boolean;
      onReview?: () => void;
      onDismiss?: () => void;
      busy?: boolean;
    }
  | { kind: 'regenerating'; canEdit: boolean; headline: string; detail: string; busy?: boolean }
  | { kind: 'stale'; canEdit: boolean; onDismiss?: () => void; busy?: boolean }
  | {
      kind: 'regeneration-failed';
      copy: FailureCopy;
      canEdit: boolean;
      onTryAgain?: () => void;
      onDismiss?: () => void;
      busy?: boolean;
    }
) & { message?: string | null };

export function SurfaceProposalBar(props: SurfaceProposalBarProps) {
  const { busy = false, canEdit, message: refusal = null } = props;

  const dismiss = (onDismiss?: () => void) =>
    onDismiss && (
      <Button size="sm" variant="default" disabled={busy} onClick={onDismiss}>
        {REVIEW_LABELS.dismiss}
      </Button>
    );

  let message: React.ReactNode;
  let actions: React.ReactNode;
  let role: 'status' | 'alert' = 'status';
  let sweep: React.ReactNode = null;

  if (props.kind === 'waiting') {
    message = (
      <>
        <Flex className={styles.headline}>
          <Flex className={styles.dot} />
          <Text size="sm">{waitingLine(props.templateName)}</Text>
        </Flex>
        {props.fromExcerpt && (
          <Text type="secondary" size="sm">
            {FROM_EXCERPT_LINE}
          </Text>
        )}
      </>
    );
    actions = (
      <>
        {dismiss(props.onDismiss)}
        {props.onReview && (
          <Button size="sm" variant="primary" disabled={busy} onClick={props.onReview}>
            {REVIEW_LABELS.review}
          </Button>
        )}
      </>
    );
  } else if (props.kind === 'regenerating') {
    message = (
      <>
        <Flex className={styles.headline}>
          <Flex aria-hidden="true" className={styles.spinner}>
            <Spin size="sm" />
          </Flex>
          <Text size="sm">{props.headline}</Text>
        </Flex>
        <Text type="secondary" size="sm">
          {props.detail}
        </Text>
      </>
    );
    sweep = (
      <Flex aria-hidden="true" className={styles.sweep}>
        <Flex className={styles.sweepSegment} />
      </Flex>
    );
  } else if (props.kind === 'stale') {
    message = <Text size="sm">{STALE_PROPOSAL_LINE}</Text>;
    actions = dismiss(props.onDismiss);
  } else {
    role = 'alert';
    const { copy, onTryAgain } = props;
    message = (
      <>
        <Text size="sm">{copy.headline}</Text>
        <Text type="secondary" size="sm">
          {copy.safety}
        </Text>
        {copy.reason && (
          <Text type="secondary" size="sm">
            {copy.reason}
          </Text>
        )}
      </>
    );
    actions = (
      <>
        {onTryAgain && copy.retry !== 'absent' && (
          <Button
            size="sm"
            variant={copy.retry === 'primary' ? 'primary' : 'default'}
            disabled={busy}
            onClick={onTryAgain}
          >
            {REVIEW_LABELS.tryAgain}
          </Button>
        )}
        {dismiss(props.onDismiss)}
      </>
    );
  }

  return (
    <Flex className={styles.column}>
      <Flex role={role} className={bar({ progressing: props.kind === 'regenerating' })}>
        <Flex vertical className={styles.message}>
          {message}
          {refusal && (
            <Text role="alert" type="secondary" size="sm">
              {refusal}
            </Text>
          )}
        </Flex>
        {canEdit && actions && <Flex className={styles.actions}>{actions}</Flex>}
        {sweep}
      </Flex>
    </Flex>
  );
}
